import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";
import {
  fetchNonValidatedAccount,
  FetchNonValidatedAccount,
} from "store/account/account-action";
import {
  AccountInfo as AccountInformation,
  FetchNonValidatedDTO,
} from "store/account/account-state";
import { clearAllNotifications } from "store/notification/notification-slice";
import { renderConditionalProp } from "utility";
import { genericRegex } from "utility/regex";
import { CAPTCHA_CONFIG } from "constants/captcha";
import { useCaptchaLoader } from "hooks/useCaptchaLoader";
import classes from "./NonValidatedForm.module.scss";

export const NonValidatedForm = () => {
  const { t } = useTranslation(["app", "common", "quickpay", "payment"]);
  const { client, paymentCenter } = useRootSelector((state) => state.config);
  const requestDispatch = useRequestDispatch();
  const dispatch = useRootDispatch();
  const navigate = useNavigate();

  const [referenceNumber, setReferenceNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [showV2Captcha, setShowV2Captcha] = useState(false);
  const [v2Token, setV2Token] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const referenceNumberRef = useRef<any>(null);
  const accountTypeRef = useRef<any>(null);
  const v2CaptchaContainerRef = useRef<HTMLDivElement>(null);

  const captchaEnabled = paymentCenter.quickPayments.captchaEnabled;

  const { loadV3Captcha, loadV2Captcha } = useCaptchaLoader(
    captchaEnabled,
    (token) => setV2Token(token),
  );

  // Explicitly render the v2 widget when it becomes visible — auto-render only fires on page load
  useEffect(() => {
    if (!showV2Captcha || !v2CaptchaContainerRef.current) return;

    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha?.render) return;

    // Only render if the container is empty (avoid double-render)
    if (v2CaptchaContainerRef.current.childElementCount === 0) {
      grecaptcha.render(v2CaptchaContainerRef.current, {
        sitekey: CAPTCHA_CONFIG.v2SiteKey,
        callback: (window as any).onV2CaptchaCallback,
      });
    }
  }, [showV2Captcha]);

  const backButtonHandler = () => {
    navigate(`/${client.customerKey}`);
  };

  const getV3Token = async (): Promise<{ token: string } | null> => {
    if (!captchaEnabled) return null;

    try {
      await loadV3Captcha();
      const grecaptcha = (window as any).grecaptcha;
      const token = await grecaptcha.execute(CAPTCHA_CONFIG.siteKey, {
        action: "quickpay_nonvalidated",
      });
      return { token };
    } catch (e) {
      console.error("reCAPTCHA v3 token generation failed:", e);
      return null;
    }
  };

  const submitHandler = async () => {
    let isValid = true;
    if (
      referenceNumberRef.current &&
      !referenceNumberRef.current.checkValidity()
    ) {
      referenceNumberRef.current.reportValidity();
      isValid = false;
    }
    if (accountTypeRef.current && !accountTypeRef.current.checkValidity()) {
      accountTypeRef.current.reportValidity();
      isValid = false;
    }
    if (!isValid) return;
    if (showV2Captcha && !v2Token) return;

    if (client.clientId) {
      const captchaToken = showV2Captcha
        ? (v2Token ?? undefined) // v2 token after user solved checkbox
        : ((await getV3Token())?.token ?? undefined); // v3 invisible token
      if (captchaEnabled && !captchaToken) return;
      const captchaVersion: "v3" | "v2" = showV2Captcha ? "v2" : "v3";

      const reqObj: FetchNonValidatedDTO = {
        clientId: client.clientId,
        referenceNumber,
        accountType,
        captchaToken,
        captchaVersion, // Tells backend which secret key to use for validation
        challengeId: challengeId ?? undefined, // ties v3 and v2 challenges together
      };

      requestDispatch<FetchNonValidatedAccount, FetchNonValidatedDTO>(
        fetchNonValidatedAccount,
        reqObj,
      )
        .unwrap()
        .then((res: AccountInformation[]) => {
          dispatch(clearAllNotifications());
          navigate(`/${client.customerKey}/quick-payment`);
        })
        .catch((error: any) => {
          const status = error?.status;
          const newChallengeId = error?.challengeId;
          if (status === 428) {
            // Backend signals: v3 score was borderline, escalate to v2
            loadV2Captcha()
              .then(() => {
                setChallengeId(newChallengeId);
                setShowV2Captcha(true);
              })
              .catch(() => {
                console.error("Unable to load CAPTCHA fallback.");
              });
          } else if (status === 403) {
            // Hard reject (bot or v2 failed) — notification already suppressed in thunk
            console.error("CAPTCHA verification failed (403).");
          } else {
            console.error("Error when getting a non-validated account:", error);
          }
        });
    }
  };

  return (
    <div className={classes.Container}>
      <div className={classes.FormFields}>
        <ft-textfield
          outlined
          required
          ref={referenceNumberRef}
          label={t("quickpay:reference_number")}
          validationMessage={
            referenceNumber &&
            !new RegExp(`^(?:${genericRegex})$`).test(referenceNumber)
              ? t("common:invalid_input")
              : t("common:invalid_field_generic")
          }
          type="text"
          onInput={(e: any) => setReferenceNumber(e.target.value)}
          value={referenceNumber}
          pattern={genericRegex}
        ></ft-textfield>
        <ft-select
          ref={accountTypeRef}
          required
          outlined
          label={t("payment:account_type")}
          validationMessage={t("common:invalid_field_generic")}
          value={accountType}
          onClick={(e: any) => setAccountType(e.target.value)}
        >
          {Object.entries(paymentCenter.accountTypes)
            .filter(([, config]) => !config.associatedRealTimeHandler)
            .map(([code, config]) => (
              <ft-list-item
                key={code}
                value={code}
                {...renderConditionalProp(accountType === code, "selected")}
              >
                {config.name}
              </ft-list-item>
            ))}
        </ft-select>
      </div>
      {showV2Captcha && <div ref={v2CaptchaContainerRef} />}
      <div className={classes.ButtonContainer}>
        <ft-button
          onClick={backButtonHandler}
          class={[classes.BackButton, "outline-button"].join(" ")}
          label={t("quickpay:back_button")}
          outlined
        ></ft-button>
        <ft-button
          class={classes.SubmitButton}
          label={t("quickpay:quickpay_make_payment")}
          unelevated
          onClick={submitHandler}
        ></ft-button>
      </div>
    </div>
  );
};
